// src/components/admin/SurfacePill.tsx
import { cn } from '@/lib/utils';
import { adminCopy } from '@/lib/copy';

export interface SurfacePillProps {
  variant: 'platform' | 'workspace' | 'clinical';
  /** Required when variant is 'workspace'. Truncated at 24 chars. */
  businessAssociateName?: string | null;
  className?: string;
}

const MAX_BA_NAME_CHARS = 24;

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

// Each variant carries a small accent dot — the only colour cue. Its
// placement (leading the eyebrow) keeps the surface caption quiet
// enough to live next to a breadcrumb without competing with it,
// while still signalling which world you're in at a glance.
const DOT_COLOR = {
  platform: 'bg-primary-400',
  workspace: 'bg-accent-ink',
  clinical: 'bg-primary-500',
} as const;

/**
 * Surface caption — a typographic "you are in {x}" eyebrow that sits
 * to the right of the topbar utility cluster. Replaces the previous
 * filled-block pill (which set off as a warning banner against the
 * otherwise restrained chrome). Reads as a printed running-head
 * marker: small accent dot + tracked uppercase italic SemiMono.
 */
export function SurfacePill({ variant, businessAssociateName, className }: SurfacePillProps) {
  let label: string;
  let detail: string | null = null;

  if (variant === 'platform') {
    label = adminCopy.shell.surfacePillPlatform;
  } else if (variant === 'clinical') {
    label = adminCopy.shell.surfacePillClinical;
  } else {
    label = adminCopy.shell.surfacePillWorkspacePrefix;
    const fullName = businessAssociateName ?? '';
    const visible = truncate(fullName, MAX_BA_NAME_CHARS);
    if (visible) detail = visible;
  }

  const fullName = businessAssociateName ?? '';
  const needsTooltip = variant === 'workspace' && detail !== null && fullName !== detail;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 select-none',
        'text-meta-sm font-display tracking-eyebrow text-neutral-500 uppercase',
        className,
      )}
      title={needsTooltip ? fullName : undefined}
    >
      <span
        aria-hidden="true"
        className={cn('h-1.5 w-1.5 shrink-0 rounded-full', DOT_COLOR[variant])}
      />
      <span className="leading-none">{label}</span>
      {detail ? (
        <>
          <span aria-hidden="true" className="text-neutral-300">
            ·
          </span>
          <span className="text-accent-ink leading-none tracking-normal normal-case">{detail}</span>
        </>
      ) : null}
    </span>
  );
}
